import React from 'react';
import { Target, Eye, CheckCircle2, Lightbulb, Globe2, TrendingUp } from 'lucide-react';

export const VisionMissionSection: React.FC = () => {
  return (
    <section className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      {/* Background ambient glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#159DE8]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF851B]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] flex items-center gap-1.5">
              <Lightbulb size={12} />
              PURPOSE & DIRECTION
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display">
            Why We Exist &{' '}
            <span className="text-[#159DE8]">Where We're Going</span>
          </h2>
          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            The convictions that guide every technology decision, partnership, and line of code we deliver.
          </p>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">

          {/* Mission Card */}
          <div className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-[#0B2342] via-[#071426] to-[#050A13] border border-[#159DE8]/30 overflow-hidden shadow-2xl group hover:border-[#159DE8]/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(21,157,232,0.1)]">
            <div className="absolute top-0 right-0 w-56 h-56 bg-[#159DE8]/8 rounded-full blur-3xl pointer-events-none group-hover:bg-[#159DE8]/12 transition-all duration-500" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#159DE8]/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 space-y-5">
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#159DE8]/15 border border-[#159DE8]/30 flex items-center justify-center shrink-0 group-hover:bg-[#159DE8]/25 group-hover:scale-105 transition-all duration-300">
                  <Target size={30} className="text-[#159DE8]" />
                </div>
                <div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#159DE8] block mb-0.5">Our Mission</span>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white font-display leading-tight">
                    Technology That Creates Real Progress
                  </h3>
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-[#159DE8]/40 via-[#159DE8]/20 to-transparent" />

              <p className="text-base text-[#D5DFEC] leading-relaxed">
                To empower startups, growing businesses, and enterprises with intelligent software architectures, robust mobile applications, and data-driven digital marketing strategies — turning operational ambition into measurable commercial reality.
              </p>

              <div className="space-y-3 pt-2">
                {[
                  { text: 'Build technology aligned to real business goals', icon: TrendingUp },
                  { text: 'Deliver measurable ROI on every engagement', icon: CheckCircle2 },
                  { text: 'Make enterprise-grade software accessible to every business', icon: Globe2 },
                ].map((point, i) => {
                  const Icon = point.icon;
                  return (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-[#D5DFEC] group/item">
                      <div className="w-6 h-6 rounded-md bg-[#159DE8]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={13} className="text-[#159DE8]" />
                      </div>
                      <span>{point.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Bottom stat */}
              <div className="pt-4 mt-2 border-t border-[#1b2f4a]/60 flex items-center gap-3">
                <span className="text-2xl font-extrabold text-[#159DE8]">250+</span>
                <span className="text-xs text-[#8FA0B8] leading-tight">Businesses transformed<br />across 15+ industry domains</span>
              </div>
            </div>
          </div>

          {/* Vision Card */}
          <div className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-[#2a1500] via-[#071426] to-[#050A13] border border-[#FF851B]/30 overflow-hidden shadow-2xl group hover:border-[#FF851B]/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(255,133,27,0.1)]">
            <div className="absolute top-0 right-0 w-56 h-56 bg-[#FF851B]/8 rounded-full blur-3xl pointer-events-none group-hover:bg-[#FF851B]/12 transition-all duration-500" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#FF5A36]/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 space-y-5">
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#FF851B]/15 border border-[#FF851B]/30 flex items-center justify-center shrink-0 group-hover:bg-[#FF851B]/25 group-hover:scale-105 transition-all duration-300">
                  <Eye size={30} className="text-[#FF851B]" />
                </div>
                <div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#FF851B] block mb-0.5">Our Vision</span>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white font-display leading-tight">
                    A Globally Trusted Technology Partner
                  </h3>
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-[#FF851B]/40 via-[#FF851B]/20 to-transparent" />

              <p className="text-base text-[#D5DFEC] leading-relaxed">
                To be an internationally recognized digital engineering and growth partner — celebrated for technical integrity, human-centric design, and software ecosystems that scale reliably and deliver enduring business value for decades.
              </p>

              <div className="space-y-3 pt-2">
                {[
                  { text: 'Recognized globally for technical excellence and integrity', icon: Globe2 },
                  { text: 'Build software that creates lasting competitive advantages', icon: TrendingUp },
                  { text: 'Become the most trusted partner for digital transformation', icon: CheckCircle2 },
                ].map((point, i) => {
                  const Icon = point.icon;
                  return (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-[#D5DFEC]">
                      <div className="w-6 h-6 rounded-md bg-[#FF851B]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={13} className="text-[#FF851B]" />
                      </div>
                      <span>{point.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Bottom stat */}
              <div className="pt-4 mt-2 border-t border-[#1b2f4a]/60 flex items-center gap-3">
                <span className="text-2xl font-extrabold text-[#FF851B]">99%</span>
                <span className="text-xs text-[#8FA0B8] leading-tight">Client retention rate —<br />long-term technology partnerships</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom value pillars strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Innovation First', desc: 'AI-driven thinking in every solution', color: 'text-[#7B2EFF]', bg: 'bg-[#7B2EFF]/8 border-[#7B2EFF]/20' },
            { label: 'Human-Centered', desc: 'Design that serves real people', color: 'text-[#159DE8]', bg: 'bg-[#159DE8]/8 border-[#159DE8]/20' },
            { label: 'Results-Driven', desc: 'Measured by business outcomes', color: 'text-[#FF851B]', bg: 'bg-[#FF851B]/8 border-[#FF851B]/20' },
            { label: 'Built to Scale', desc: 'Architecture for 10x growth', color: 'text-[#00C853]', bg: 'bg-[#00C853]/8 border-[#00C853]/20' },
          ].map((v, i) => (
            <div key={i} className={`p-4 rounded-2xl border ${v.bg} text-center hover:scale-[1.02] transition-transform`}>
              <span className={`block text-sm font-bold mb-1 ${v.color}`}>{v.label}</span>
              <span className="block text-xs text-[#8FA0B8] leading-snug">{v.desc}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
